import MaterialTable, { MTableBodyRow, MTableCell } from '@material-table/core'
import { Chip, Container, Link, Typography } from '@material-ui/core'
import { makeStyles, MuiThemeProvider, useTheme } from '@material-ui/core/styles'
import { cloneDeep, isEqual } from 'lodash'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom'

import { reloadFacilities } from '../../../actions/facilities/facilities.actions'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { KennelsToolbar } from '../../../components/Tables/Toolbars/Kennels/KennelsToolbar'
import { getChipStyles } from '../../../services/helpers'
import { table } from '../../../services/theme'
import { getTableRowId, KENNEL_STATUSES, sortAlphaNum } from '../../../utils'

const cutoffs = [
	{ name: 'The Day Of', number: 0 },
	{ name: 'One Day Before', number: 1 },
	{ name: 'Two Days Before', number: 2 }
]
const useStyles = makeStyles((theme) => ({
	root: {
		// flexGrow: 1,
		paddingTop: '24px',
		paddingLeft: '25px',
		paddingRight: '20px',
		paddingBottom: '75px'
	},
	appBar: {
		top: 'auto',
		bottom: 0,
		paddingRight: '0px !important'
	},
	grid: {},
	gridDouble: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'flex-start'
	},
	fieldWrapper: {
		// '& .MuiTextField-root': { width: '100%', minWidth: '100%' }
	},
	header: {
		fontSize: 12,
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8
	},
	header2: {
		fontSize: 12,
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8
	},
	purple: {
		backgroundColor: '#F0EDFB',
		width: '400px',
		padding: 12,
		paddingTop: 16,
		paddingBottom: 16,
		borderRadius: 10
	},
	purpleText: {
		fontSize: 14,
		lineHeight: '21px',
		color: '#270050',
		padding: 0,
		margin: 0
	},
	toolbarWrapper: {
		display: 'flex',
		flexDirection: 'column',
		padding: '24px 24px 24px 0px',
		color: '#1A1A35'
	},
	chipRow: {
		height: '100%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center'
	}
}))

const FacilityBasicInfo = () => {
	const classes = useStyles()
	const params = useParams()
	const theme = useTheme()
	const dispatch = useDispatch()
	const history = useHistory()
	const location = useLocation()

	const tableRef = useRef(null)
	const toolbarRef = useRef(null)

	const selectedFacility = useSelector((state) => state.specific.facilities.selected)
	const selectedFilters = useSelector((state) => state.specific.facilities.selectedKennelsFilters)
	const loading = useSelector((state) => state.specific.facilities.loading)

	const [current, setCurrent] = useState(null)
	const [isSaveShown, setIsSaveShown] = useState(false)
	const [data, setData] = useState([])

	const [date, setDate] = useState(moment().format('YYYY-MM-DD'))
	const [hoveringOver, setHoveringOver] = useState('')
	const [kennelToEdit, setKennelToEdit] = useState(null)

	useEffect(() => {
		dispatch(reloadFacilities(false))
	}, [])

	useEffect(() => {
		if (params.facilityId && selectedFacility) {
			setCurrent(cloneDeep(selectedFacility))
		}
	}, [params.facilityId, selectedFacility])

	useEffect(() => {
		if (current && selectedFacility) {
			let facilityEqual = !isEqual(current, selectedFacility)

			setIsSaveShown(facilityEqual)
		}
	}, [current, selectedFacility])

	useEffect(() => {
		let filteredData = [...(current?.kennels || [])]

		if (Object.keys(selectedFilters).some((f) => selectedFilters[f])) {
			if (selectedFilters.area.length) {
				filteredData = filteredData.filter((d) => selectedFilters.area.includes(d.area_id))
			}
			if (selectedFilters.kennel_block.length) {
				filteredData = filteredData.filter((d) => selectedFilters.kennel_block.includes(d.kennel_block_id))
			}
			if (selectedFilters.type.length) {
				filteredData = filteredData.filter((d) => selectedFilters.type.includes(d.kennel_type_id))
			}
			if (selectedFilters.status.length) {
				filteredData = filteredData.filter((d) => selectedFilters.status.includes(d.status))
			}
		}
		// sort kennels
		filteredData = filteredData.sort((a, b) => sortAlphaNum(a.reference_number, b.reference_number))
		setData(filteredData)
	}, [current, selectedFilters])

	const handleRowHover = (_event, propsData) => {
		setTimeout(() => {
			setHoveringOver(getTableRowId(propsData))
		}, 100)
	}
	const handleRowHoverLeave = (_event, propsData) => setHoveringOver('')

	const editRow = (row) => {
		setKennelToEdit(row)
	}

	// RENDER COLUMNS CUSTOM
	const renderKennelNumber = (data) => {
		const val = data.reference_number || ''

		return (
			<div>
				<Typography
					style={{
						textTransform: 'capitalize',
						color: '#1A1A35',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: '22px'
					}}>
					{val}
				</Typography>
				{/* <Button
					variant="text"
					style={{
						paddingLeft: 0,
						fontWeight: '600',
						fontSize: 12,
						textTransform: 'capitalize',
						color: '#6B4AD6'
					}}
					onClick={() => editRow(data)}>Edit Kennel</Button> */}
				<Link
					component={RouterLink}
					style={{
						color: theme.palette.secondary.main,
						marginBottom: 5,
						fontSize: '14px',
						lineHeight: '19px'
					}}
					onClick={(e) => {
						e.stopPropagation()
						editRow(data)
					}}>
					Edit Kennel
				</Link>
			</div>
		)
	}

	const renderKennelArea = (data) => {
		const found = current.areas?.find((p) => p._id === data.area_id)
		return found?.name
	}

	const renderKennelBlock = (data) => {
		const found = current.kennel_blocks?.find((p) => p._id === data.kennel_block_id)
		const val = found?.name || ''

		return val ? (
			<div>
				<Chip label={val} style={getChipStyles('purple', val.length > 15)} />
			</div>
		) : null
	}

	const renderKennelType = (data) => {
		const found = current.kennel_types?.find((p) => p._id === data.kennel_type_id)
		return found?.name
	}

	const renderStatus = (data) => {
		const val = data?.status in KENNEL_STATUSES ? KENNEL_STATUSES[data.status] : 'No Status'

		return (
			<div className={classes.chipRow}>
				<Chip label={val} style={val ? getChipStyles(val) : null} />
			</div>
		)
	}

	// const renderPen = (data) => {
	// 	const id = getTableRowId(data)
	// 	if (id === hoveringOver) {
	// 		return <Edit style={{ fill: '#777679', fontSize: 20, fontWeight: '900' }} />
	// 	}

	// 	return null
	// }

	return (
		<div>
			{current ? (
				<Container className={classes.root} maxWidth="auto">
					<MuiThemeProvider theme={table}>
						<KennelsToolbar
							ref={toolbarRef}
							tableRef={tableRef}
							title={'Kennels'}
							date={date}
							setDate={setDate}
							changeDate={() => {}}
							allowDateChange={false}
							allowFilter={true}
							allowAdd={true}
							kennelToEdit={kennelToEdit}
						/>
						<MaterialTable
							tableRef={tableRef}
							date={date}
							style={{ paddingLeft: 24, paddingRight: 24, marginTop: 30 }}
							options={{
								// toolbar: false,
								search: false,
								headerStyle: {
									// header columns style
									color: '#1A1A35',
									textTransform: 'uppercase',
									fontWeight: 800,
									fontSize: '12px',
									lineHeight: '16px',
									padding: '0px 24px 0px 0px'
									// borderTop: '1px solid #D0CFD2',
									// backgroundColor: '#F9F9F9'
								},
								rowStyle: {
									verticalAlign: 'top',
									fontSize: 16
								},
								tableLayout: 'fixed',
								pageSize: 20,
								pageSizeOptions: [5, 10, 20, 50],
								toolbarButtonAlignment: 'left',
								searchFieldAlignment: 'left'
							}}
							columns={[
								{
									title: 'Number',
									// field: 'reference_number',
									width: 210,
									cellStyle: {
										verticalAlign: 'top',
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderKennelNumber,
									customFilterAndSearch: (term, rowData) => {
										return rowData?.reference_number
											?.toUpperCase()
											.includes(term.toUpperCase())
									},
									customSort: (a, b) => {
										a = (a && a.reference_number) || ''
										b = (b && b.reference_number) || ''

										return sortAlphaNum(a, b)
									}
								},
								{
									title: 'Area',
									// field: 'area',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderKennelArea,
									customSort: (a, b) => {
										a = current.areas?.find((p) => p._id === a.area_id)
										b = current.areas?.find((p) => p._id === b.area_id)

										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b)
									}
								},
								{
									title: 'Kennel block',
									// field: 'kennels',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderKennelBlock,
									customSort: (a, b) => {
										a = current.kennel_blocks?.find((p) => p._id === a.kennel_block_id)
										b = current.kennel_blocks?.find((p) => p._id === b.kennel_block_id)

										a = (a && a.name) || ''
										b = (b && b.name) || ''

										return sortAlphaNum(a, b)
									}
								},
								{
									title: 'Type',
									// field: 'kennel_type_id',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '14%',
									render: renderKennelType
								},
								{
									title: 'Status',
									// field: 'status',
									cellStyle: {
										padding: '11px 0px',
										border: 'none',
										wordWrap: 'break-word'
									},
									width: '10%',
									render: renderStatus,
									customSort: (a, b) => {
										a = a?.status in KENNEL_STATUSES ? KENNEL_STATUSES[a.status] : 'No Status'
										b = b?.status in KENNEL_STATUSES ? KENNEL_STATUSES[b.status] : 'No Status'

										return sortAlphaNum(a, b)
									}
								}
								// hover column
								// {
								// 	title: '',
								// 	// field: '',
								// 	cellStyle: {
								// 		padding: '11px 0px',
								// 		border: 'none',
								// 		wordWrap: 'break-word'
								// 	},
								// 	width: '5%',
								// 	render: renderPen
								// }
							]}
							data={data}
							onRowClick={(e, row) => {
								// open popup to edit
								setHoveringOver(getTableRowId(row))
								// editRow(row)
							}}
							components={{
								Cell: (props) => (
									<MTableCell
										style={{
											borderBottom: 'none',
											padding: '16px 36px 16px 24px'
										}}
										{...props}
									/>
								),
								Row: (props) => (
									<MTableBodyRow
										{...props}
										onMouseEnter={(e) => handleRowHover(e, props)}
										onMouseLeave={(e) => handleRowHoverLeave(e, props)}
									/>
								),
								Toolbar: (props) => (
									<div className={classes.toolbarWrapper}>
										<Typography
											style={{
												textTransform: 'uppercase',
												fontSize: 16,
												fontWeight: '800',
												lineHeight: '22px'
											}}>
											Kennels
										</Typography>
									</div>
								)
							}}
						/>
					</MuiThemeProvider>
				</Container>
			) : null}
			{loading ? <LoadingOverlay /> : null}
		</div>
	)
}

export default FacilityBasicInfo
